import { negotiateLocale } from '@yourstore/domain';

/**
 * Which language this bundle's own words are in.
 *
 * ## The rule for this surface, and it is not the guest site's
 *
 * **The staff UI's language is the staff member's, not the tenant's.** A counter
 * in Frenchtown may have an English-reading owner and a Spanish-reading
 * therapist sharing one shop, and the book they read is the same book. So the
 * language comes from **the device's browser** (`navigator.languages`), narrowed
 * to the set of catalogs we actually have — never from `tenants.default_locale`,
 * which is a fact about the *business* and about what its *guests* read. That
 * column is exactly right for `apps/site`, which follows it via `site.locale`
 * (docs/i18n-design.md §3), and exactly wrong here: it would tell a Dominican
 * therapist at an English-speaking spa to work in a language she did not choose,
 * and it would do it on a device she is holding.
 *
 * It is resolved **once, at module load**, because a browser's language list
 * does not change during a session. There is no re-negotiation on a render and
 * no listener, so there is one answer per app launch and it cannot drift from
 * the `Intl` calls that share it.
 *
 * A per-staff-member preference is the obvious next step — a column on the staff
 * user, chosen in Settings — and this is the seam it plugs into: it would sit in
 * front of `browserLocales()` in the same call, and nothing else here changes.
 * It is not built because there is nowhere on the server to keep it yet.
 *
 * The negotiation itself is `@yourstore/domain`'s. There is exactly one ladder
 * in this system for the same reason there is one scheduling engine, and
 * `negotiateLocale` is it — `es-419`, `es-MX` and `ES` all land on `es` without
 * this file knowing how.
 */

/** Every language the UI catalogs cover. Adding one is a file, not a refactor. */
export const UI_LOCALES = ['en', 'es'] as const;
export type UiLocale = (typeof UI_LOCALES)[number];

/**
 * English, because it is the USVI's official language and because a device that
 * has expressed no preference at all is overwhelmingly being read in it (§1).
 */
export const DEFAULT_UI_LOCALE: UiLocale = 'en';

/**
 * Narrow anything BCP-47 to a language this bundle actually speaks.
 *
 * Preferences are given most-wanted first. A tag we have no catalog for —
 * `fr-HT`, the designed-for third — falls to English rather than to a gap: a
 * Haitian owner works in English today and in French the day `fr.ts` exists,
 * with nothing else changing.
 */
export function narrowUiLocale(...preferences: readonly (string | null | undefined)[]): UiLocale {
  const picked = negotiateLocale(preferences, UI_LOCALES, DEFAULT_UI_LOCALE);
  // `negotiateLocale` answers with a member of `supported`, but proving that to
  // the compiler with a cast would be asserting rather than checking.
  return UI_LOCALES.find((locale) => locale === picked) ?? DEFAULT_UI_LOCALE;
}

/**
 * What the browser says it reads, most-wanted first.
 *
 * `navigator.languages` rather than `navigator.language`, because a staff member
 * whose first choice we have no catalog for may well have a second we do.
 * Guarded because this bundle is also rendered in tests and by a jsdom that may
 * have neither.
 */
export function browserLocales(): string[] {
  const nav: { languages?: readonly string[]; language?: string } | undefined =
    globalThis.navigator as { languages?: readonly string[]; language?: string } | undefined;
  if (nav === undefined) return [];
  if (Array.isArray(nav.languages)) return [...nav.languages];
  return typeof nav.language === 'string' ? [nav.language] : [];
}

/**
 * The answer, decided once when this module is first imported.
 *
 * Everything that is not a React component reads *this* — `mirror/time.ts`'s
 * formatters, `actions.ts`'s intent descriptions, `outbox/tasks.ts` — so a
 * module-scope helper and a component render the same language without either
 * of them having to be told which. A `<MessagesProvider>` overrides it for the
 * tree below it, which is what the tests use and what a per-staff preference
 * would use.
 */
export const DEVICE_LOCALE: UiLocale = narrowUiLocale(...browserLocales());
